// =====================================================================
// unsereHochzeits.app – Bar-Intro  (Animierter Wizard für den Barkeeper)
// =====================================================================
// Erst-Öffnen-Einführung im Onboarding-Look: animierte Card, Progress,
// Confetti am Ende. Erklärt den Barkeeper-Screen in wenigen Schritten.
// Schritte: 0 Willkommen · 1 Live · 2 Fertig-Klick · 3 Aufnehmen · 4 Los geht's
// =====================================================================

import { useState, useEffect, useRef } from "react";
import "../styles/theme.css";

const STEPS = [
  {
    emoji: "🍸",
    title: "Willkommen hinter der Bar",
    body: <>Auf diesem Bildschirm laufen alle Getränke-Bestellungen der Feier zusammen –
      live und automatisch. In 20 Sekunden bist du eingearbeitet.</>,
  },
  {
    emoji: "🔔",
    title: "Bestellungen kommen live",
    body: <>Neue Bestellungen erscheinen von selbst – <strong>pro Tisch gruppiert</strong>,
      das Älteste oben. Der Zähler oben zeigt, wie viele noch offen sind.</>,
  },
  {
    emoji: "✓",
    title: "Ein Klick = fertig",
    body: <>Ist ein Getränk ausgegeben, tippe <strong>„Als fertig markieren"</strong>.
      Es wandert ins eingeklappte Archiv – so bleibt die Liste übersichtlich.</>,
  },
  {
    emoji: "＋",
    title: "Selbst aufnehmen",
    body: <>Bestellt jemand direkt am Tresen? Mit <strong>„＋ Bestellung aufnehmen"</strong>
      wählst du Tisch und Getränke und buchst sie in Sekunden ein.</>,
  },
];

export default function BarIntro({ onDone }) {
  const [step, setStep] = useState(0);
  const isFinish = step === STEPS.length;

  return (
    <div style={S.page}>
      <style>{CSS}</style>

      {!isFinish && (
        <div style={S.progressWrap}>
          <div style={S.progressTrack}>
            <div style={{ ...S.progressFill, width: `${((step + 1) / (STEPS.length + 1)) * 100}%` }} />
          </div>
          <p style={S.progressText}>Schritt {step + 1} von {STEPS.length + 1}</p>
        </div>
      )}

      <div key={step} className="barwiz-card" style={{ animation: "wizIn 0.45s cubic-bezier(0.34,1.56,0.64,1)" }}>
        <p style={S.monogram}>unsereHochzeits.app · Bar</p>

        {isFinish ? (
          <FinishStep onDone={onDone} />
        ) : (
          <div style={{ textAlign: "center" }}>
            <div className="barwiz-emoji">{STEPS[step].emoji}</div>
            <h1 style={S.h1}>{STEPS[step].title}</h1>
            <p style={S.sub}>{STEPS[step].body}</p>
            <button className="wos-btn wos-btn-primary" onClick={() => setStep(step + 1)}>
              {step === STEPS.length - 1 ? "Fast fertig →" : "Weiter →"}
            </button>
            <button className="wos-btn wos-btn-ghost" onClick={onDone} style={{ marginTop: 8 }}>
              Überspringen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FinishStep({ onDone }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width  = canvas.offsetWidth;
    const H = canvas.height = canvas.offsetHeight;
    const COLORS = ["#C9956C", "#2C3B2D", "#E8C9A0", "#B76E79", "#F2E8D5"];
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * W, y: Math.random() * -H,
      r: 4 + Math.random() * 6, c: COLORS[Math.floor(Math.random() * COLORS.length)],
      vy: 1.5 + Math.random() * 3, vx: -1 + Math.random() * 2,
      rot: Math.random() * Math.PI, vrot: -0.1 + Math.random() * 0.2,
    }));
    let raf, t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pieces.forEach(p => {
        p.y += p.vy; p.x += p.vx; p.rot += p.vrot;
        if (p.y > H + 20) { p.y = -20; p.x = Math.random() * W; }
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.c; ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 0.6);
        ctx.restore();
      });
      t += 1;
      if (t < 260) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div style={{ textAlign: "center", position: "relative" }}>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div className="barwiz-emoji barwiz-pop">🎉</div>
        <h1 style={S.h1}>Du bist startklar!</h1>
        <p style={S.sub}>Die Bestellungen laufen ab jetzt automatisch ein. Auf einen guten Abend!</p>
        <button className="wos-btn wos-btn-primary" onClick={onDone}>Los geht's →</button>
      </div>
    </div>
  );
}

const CSS = `
@keyframes wizIn {
  0%   { opacity: 0; transform: translateX(40px) scale(0.96); }
  60%  { opacity: 1; }
  100% { opacity: 1; transform: translateX(0) scale(1); }
}
@keyframes wizEmoji {
  0%   { transform: scale(0) rotate(-20deg); }
  60%  { transform: scale(1.3) rotate(10deg); }
  100% { transform: scale(1) rotate(0); }
}
@keyframes wizPop {
  0% { transform: scale(0); } 50% { transform: scale(1.4); }
  70% { transform: scale(0.9); } 100% { transform: scale(1); }
}
.barwiz-card {
  background: var(--surface, #fff); border: 1px solid var(--line);
  border-radius: var(--radius-lg, 20px); padding: 32px 28px;
  max-width: 440px; width: 100%;
  box-shadow: 0 8px 40px rgba(44,59,45,0.08);
  position: relative; overflow: hidden;
}
.barwiz-emoji {
  font-size: 56px; line-height: 1; margin-bottom: 16px; display: inline-block;
  animation: wizEmoji 0.6s cubic-bezier(0.34,1.56,0.64,1);
}
.barwiz-pop { animation: wizPop 0.7s cubic-bezier(0.34,1.56,0.64,1); }
@media (prefers-reduced-motion: reduce) {
  .barwiz-card, .barwiz-emoji, .barwiz-pop { animation: none !important; }
}
`;

const S = {
  page: {
    minHeight: "100dvh", background: "var(--bg)", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", padding: "24px 20px 48px",
    fontFamily: "var(--font-body)", color: "var(--ink)",
  },
  progressWrap: { width: "100%", maxWidth: 440, marginBottom: 20 },
  progressTrack: { height: 4, background: "var(--line)", borderRadius: 999, overflow: "hidden" },
  progressFill: { height: "100%", background: "var(--accent)", borderRadius: 999, transition: "width 0.45s cubic-bezier(0.34,1.56,0.64,1)" },
  progressText: { fontSize: 11, color: "var(--ink-subtle)", textAlign: "center", marginTop: 8, letterSpacing: "0.1em", textTransform: "uppercase" },
  monogram: { fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 11, letterSpacing: "0.3em", color: "var(--accent)", margin: "0 0 16px", textAlign: "center" },
  h1: { fontFamily: "var(--font-display)", fontSize: "1.7rem", fontWeight: 500, margin: "0 0 8px" },
  sub: { fontSize: 13, color: "var(--ink-muted)", lineHeight: 1.6, margin: "0 0 24px" },
};
